import { useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { createContext, PropsWithChildren, useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature, SellerUserClient, UserSetting } from 'src/WebApiController';

type UserSettingsMap = {
  [key in keyof typeof UserSetting]?: string;
};

type ISellerUserAllSettingsContext = {
  queryResult?: UserSettingsMap | null;
  contextSettingIds?: UserSetting[];
  contextQueryKey?: (string | boolean | UserSetting[] | undefined)[];
  isLoading?: boolean;
  refresh?: () => void;
} | null;

export const SellerUserAllSettingsContext =
  createContext<ISellerUserAllSettingsContext>(null);

export const SellerUserAllSettingsProvider = ({
  children,
}: PropsWithChildren) => {
  const hasReworkUserSettingsContextFeature = useUserHasFeature(
    Feature.FEPerf_ReworkUserSettingsContext
  );

  // When feature flag is enabled, return early with stable null value
  // This prevents all the expensive hooks below from running
  const emptyContextValue = useMemo(() => null, []);
  if (hasReworkUserSettingsContextFeature) {
    return (
      <SellerUserAllSettingsContext.Provider value={emptyContextValue}>
        {children}
      </SellerUserAllSettingsContext.Provider>
    );
  }

  // Legacy implementation - only runs when feature flag is disabled
  return (
    <LegacySellerUserAllSettingsProvider>
      {children}
    </LegacySellerUserAllSettingsProvider>
  );
};

// TODO: Remove when FEPerf_ReworkUserSettingsContext is GA'ed
// Separate component for legacy implementation to avoid running hooks when feature flag is enabled
const LegacySellerUserAllSettingsProvider = ({
  children,
}: PropsWithChildren) => {
  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const sellerUserClient = useMemo(
    () => new SellerUserClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  const queryKey = useMemo(
    () => ['allUserSettings', activeAccountWebClientConfig.activeAccountId],
    [activeAccountWebClientConfig.activeAccountId]
  );

  const shouldQuery = activeAccountWebClientConfig?.activeAccountId != null;

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      return await sellerUserClient.getAllSellerUserSettings(
        // These have special handling in the UI so we want to make sure we load them
        // see useUserSetting.ts
        [
          UserSetting.QuickFiltersCustomInventory,
          UserSetting.QuickFiltersCustomSales,
          UserSetting.QuickFiltersCustomPurchases,
          UserSetting.QuickFiltersCustomOnSaleEvent,
          UserSetting.QuickFiltersCustomMarketplacePayments,
        ],
        true
      );
    },

    meta: {
      persist: false,
      onError: (error: ErrorTypes) => {
        trackError('SellerUserClient.getAllSellerUserSettings', error, {
          trackErrorData: {},
        });
      },
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryClient = useQueryClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refresh = useCallback(
    debounce(() => {
      queryClient.invalidateQueries({
        queryKey: queryKey,
      });
    }, 500), // 500ms debounce so bunch of saves to user settings don't trigger a ton of invalidations
    [queryClient, queryKey]
  );

  const isLoading = useMemo(
    () =>
      !queryResult.isFetched ||
      queryResult.isRefetching ||
      queryResult.isPending,
    [queryResult.isFetched, queryResult.isPending, queryResult.isRefetching]
  );

  const contextSettingIds = useMemo(
    () =>
      Object.keys(queryResult.data ?? {}).map(
        (key) => UserSetting[key as keyof typeof UserSetting]
      ),
    [queryResult.data]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      queryResult: queryResult.data,
      isLoading,
      refresh,
      contextSettingIds,
      contextQueryKey: queryKey,
    }),
    [queryResult.data, isLoading, refresh, contextSettingIds, queryKey]
  );

  return (
    <SellerUserAllSettingsContext.Provider value={contextValue}>
      {children}
    </SellerUserAllSettingsContext.Provider>
  );
};
